/**
 * ExpandablePropertyValue — read-only property value that expands when long.
 *
 * Used by the detail read views (entity slideout + full-page record overview).
 * Renders the value MULTI-LINE (deliberately NOT the single-line `truncate`
 * mode of {@link PropertyValueLabel}) inside {@link CollapsibleContent}, which:
 *
 *   - clamps the value to `maxCollapsedHeight` and fades the clipped body, and
 *   - reveals a "Show more / Show less" toggle ONLY when the content actually
 *     overflows that height.
 *
 * So short values (email, phone, dates, one-line text) look exactly as before
 * and get no toggle, while long values (text areas, notes, descriptions) wrap
 * across multiple lines and become expandable instead of hard-truncating to a
 * single line.
 *
 * Renders a block (`<div>`, via CollapsibleContent) rather than a `<span>`, so
 * block-level value renderers such as markdown don't nest a `<div>` inside a
 * `<span>`.
 *
 * The inner value renderer defaults to {@link PropertyValueLabel} but can be
 * swapped via `ValueComponent` — e.g. the record page's overview rows use
 * {@link ExpandableMultiValueLabel} to also collapse multi-select overflow.
 * This is the single "clamp a value with Show more/less" recipe; don't
 * re-inline this component's `CollapsibleContent` + `resetKey` elsewhere.
 */

import type { ComponentType } from 'react';
import { memo, useMemo } from 'react';

import { CollapsibleContent } from '@/lib/base/collapsible-content';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

import { PropertyValueLabel } from './PropertyValueLabel';

export interface ExpandablePropertyValueRendererProps {
  value: unknown;
  propertyType: PropertyTypeSchema | undefined;
  /** Organization ID for clickable mention navigation inside text values. */
  orgId?: number;
  /**
   * The entity's raw `properties` map (e.g. `object.properties`). Only
   * meaningful when the value is a `formula` field — see
   * {@link PropertyValueLabelProps.entityProperties}.
   */
  entityProperties?: Record<string, unknown>;
}

export interface ExpandablePropertyValueProps extends ExpandablePropertyValueRendererProps {
  /**
   * Visible height (px) before the value clamps and reveals "Show more".
   * Defaults to ~5 lines of the overview row's text-xs/sm body.
   */
  maxCollapsedHeight?: number;
  /**
   * Component rendered inside the clamp. Defaults to {@link PropertyValueLabel}.
   *
   * MUST be a stable reference — a module-scope component (like
   * {@link PropertyValueLabel} or `ExpandableMultiValueLabel`) — never a
   * component or arrow function defined inline at the call site. This
   * component is wrapped in `memo`; a freshly-created value here on every
   * render would give every consumer a new prop identity each render and
   * defeat that memoization.
   */
  ValueComponent?: ComponentType<ExpandablePropertyValueRendererProps>;
}

// ~5 lines of the overview row's small body text — long enough to read a short
// paragraph inline, short enough to keep dense records scannable.
const DEFAULT_MAX_COLLAPSED_PX = 96;

export const ExpandablePropertyValue = memo(function ExpandablePropertyValue({
  value,
  propertyType,
  orgId,
  maxCollapsedHeight = DEFAULT_MAX_COLLAPSED_PX,
  entityProperties,
  ValueComponent = PropertyValueLabel,
}: ExpandablePropertyValueProps) {
  // Stable identity of the rendered value. When a reused instance switches to
  // a different value, this changes and CollapsibleContent resets to collapsed
  // — keyed off the content, not off the `children` element's reference (which
  // would collapse expanded values on unrelated parent rerenders).
  const resetKey = useMemo(
    () =>
      typeof value === 'object' && value !== null
        ? JSON.stringify(value)
        : String(value),
    [value]
  );

  return (
    <CollapsibleContent
      maxCollapsedHeight={maxCollapsedHeight}
      resetKey={resetKey}
    >
      <ValueComponent
        value={value}
        propertyType={propertyType}
        orgId={orgId}
        entityProperties={entityProperties}
      />
    </CollapsibleContent>
  );
});

ExpandablePropertyValue.displayName = 'ExpandablePropertyValue';
